import React, { useMemo, useRef } from 'react';
import PaginationTable, {
    PaginationTableProps,
} from '../../../../../../ui/DataDisplay/Tables/PaginationTable/PaginationTable';
import { TiktokSongSuggestionsTableRow } from '../TiktokSongSuggestions.types';
import { CellProps, Column } from 'react-table';
import Skeleton from '../../../../../../ui/Skeleton/Skeleton';
import { formatDate } from '../../../../../../helpers';
import styles from './TiktokSongSuggestionsTable.module.css';
import cn from 'classnames';
import CheckIcon from '../../../../../../SharedComponents/svg/Icons/CheckIcon';
import CloseIcon from '../../../../../../SharedComponents/svg/Icons/CloseIcon';
import Image from '../../../../../../ui/Image/Image';
import { ReactComponent as MusicIcon } from '../../../../../../assets/MusicIcon.svg';
import { SongListContextProvider } from '../../../../../Song/contexts/SongListContext';
import LinkToSongs from '../../../../../Song/features/LinkToSongs/LinkToSongs';
import { Popover } from '@round/ui-kit';
import { ReactComponent as InfoCircleIcon } from '../../../../../../assets/InfoCircle.svg';
import Card from '../../../../../../ui/Card/Card';
import { useAudioListPlaybackHotkeys } from '../../../../../AudioPlayer/useAudioListPlaybackHotkeys';
import TiktokAudioBadge from '../../../components/TiktokAudioBadge/TiktokAudioBadge';

type SongSuggestionsTableProps = Omit<PaginationTableProps<TiktokSongSuggestionsTableRow>, 'columns'> & {
    onAcceptSuggestionClicked: (suggestion: TiktokSongSuggestionsTableRow) => void;
    onRejectSuggestionClicked: (suggestion: TiktokSongSuggestionsTableRow) => void;
    onSongsLinked: (suggestion: TiktokSongSuggestionsTableRow, songIds: number[]) => void;
};

const TiktokSongSuggestionsTable = ({
    onAcceptSuggestionClicked,
    onRejectSuggestionClicked,
    onSongsLinked,
    ...props
}: SongSuggestionsTableProps) => {
    const hotkeysInfoRef = useRef<SVGSVGElement | null>(null);
    const audioUrls = useMemo(() => props.data.map((row) => row.audioUrl), [props.data]);
    useAudioListPlaybackHotkeys(audioUrls);

    const columns: Column<TiktokSongSuggestionsTableRow>[] = useMemo(
        () => [
            {
                Header: 'Song',
                accessor: 'song',
                Cell: ({ value }) => {
                    return (
                        <div className={styles.songContainer}>
                            <Image
                                loading={props.loading}
                                className={styles.songImage}
                                src={value?.image ?? ''}
                                alt={value?.title}
                            />
                            <div className={styles.songDetails}>
                                <a
                                    className={styles.songTitle}
                                    href={value?.spotify_song_url}
                                    rel="noopener noreferrer"
                                    target="_blank"
                                >
                                    {props.loading ? <Skeleton /> : value?.title}
                                </a>
                                <p className={styles.songAuthor}>
                                    {props.loading ? (
                                        <Skeleton />
                                    ) : (
                                        <>
                                            <MusicIcon className={styles.musicIcon} />
                                            {value?.artists.map((a) => a.name).join(', ')}
                                        </>
                                    )}
                                </p>
                            </div>
                        </div>
                    );
                },
            },
            {
                Header: () => (
                    <div className={cn(styles.row, styles.audioNameHeader)}>
                        <span>Audio Name</span>
                        <InfoCircleIcon width={14} height={14} ref={hotkeysInfoRef} />
                        <Popover anchorElement={hotkeysInfoRef.current} showOn="hover">
                            <Card className={styles.hotkeysContainer}>
                                <div>
                                    <kbd>a</kbd> - play previous audio
                                </div>
                                <div>
                                    <kbd>s</kbd> - play/pause
                                </div>
                                <div>
                                    <kbd>d</kbd> - play next audio
                                </div>
                            </Card>
                        </Popover>
                    </div>
                ),
                accessor: 'audio_title',
                Cell: ({ row: { original } }) => {
                    return (
                        <TiktokAudioBadge
                            loading={props.loading}
                            audio={{
                                tiktokId: original.audio_tiktok_id,
                                title: original.audio_title,
                                isOriginal: original.audio_is_original,
                                audioPlayUrl: original.audioUrl,
                                imageUrl: original.image,
                            }}
                        />
                    );
                },
            },
            {
                Header: 'Author Name',
                accessor: 'audio_author_name',
                Cell: ({ value }) => {
                    if (props.loading) {
                        return <Skeleton />;
                    }

                    return <>{value}</>;
                },
            },
            {
                Header: 'Date Detected',
                accessor: 'created',
                Cell: ({ value }) => {
                    if (props.loading) {
                        return <Skeleton />;
                    }

                    return <>{formatDate(value)}</>;
                },
            },
            {
                Header: 'Actions',
                id: 'actions',
                Cell: ({ row: { original } }: CellProps<TiktokSongSuggestionsTableRow>) => {
                    if (props.loading) {
                        return <Skeleton />;
                    }

                    return (
                        <div className={styles.actions}>
                            <button
                                className={styles.actionButton}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onAcceptSuggestionClicked(original);
                                }}
                            >
                                <CheckIcon
                                    className={cn(styles.actionButtonIcon, styles.acceptButton)}
                                    width={18}
                                    height={18}
                                />
                                Add
                            </button>

                            <button
                                className={styles.actionButton}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onRejectSuggestionClicked(original);
                                }}
                            >
                                <CloseIcon
                                    className={cn(styles.actionButtonIcon, styles.rejectButton)}
                                    width={11}
                                    height={11}
                                />
                                Reject
                            </button>

                            <LinkToSongs linkSongs={(songIds) => onSongsLinked(original, songIds)} />
                        </div>
                    );
                },
            },
        ],
        [onAcceptSuggestionClicked, onRejectSuggestionClicked, onSongsLinked, props.loading]
    );

    return (
        <SongListContextProvider>
            <PaginationTable columns={columns} {...props} />
        </SongListContextProvider>
    );
};

export default TiktokSongSuggestionsTable;
